import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/axios';
import Pagination from '../../components/Pagination';
import SortableTableHeader from '../../components/SortableTableHeader';
import { CopyHash } from '../../features/web3/forex/components/CopyHash';
import { bscAddressUrl } from '../../lib/format';

const CreditsDepositPerUser = () => {
  const { token } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ page: 1, pageSize: 10, username: '' });
  const [tempFilters, setTempFilters] = useState({ page: 1, pageSize: 10, username: '' });

  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [sortedData, setSortedData] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRows: 0 });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: filters.page, pageSize: filters.pageSize };
      if (filters.username) params.username = filters.username;

      const response = await axiosInstance.get('/api/admin/wallets/credits/deposits', {
        headers: { token },
        params
      });

      if (response.data.success) {
        const newData = response.data.data.data;
        setData(newData);
        setSortedData(newData);
        setPagination({
          currentPage: parseInt(response.data.data.currentPage) || 1,
          totalPages: response.data.data.totalPages || 1,
          totalRows: response.data.data.totalRows || 0
        });
      }
    } catch (error) {
      console.error('Error fetching credits deposit per user:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key] ?? '';
      let bValue = b[sort.key] ?? '';

      if (sort.key === 'totalPurchased' || sort.key === 'purchaseCount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (sort.key === 'firstPurchase' || sort.key === 'lastPurchase') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const handleTempFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters({ ...tempFilters, page: 1 });
  };

  const clearFilters = () => {
    const defaults = { page: 1, pageSize: 10, username: '' };
    setFilters(defaults);
    setTempFilters(defaults);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    setTempFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const exportToExcel = async () => {
    try {
      const params = { page: 1, pageSize: 10000 };
      if (filters.username) params.username = filters.username;

      const response = await axiosInstance.get('/api/admin/wallets/credits/deposits', {
        headers: { token },
        params
      });

      const exportData = response.data.data.data;
      if (!exportData.length) {
        alert('No data to export');
        return;
      }

      const headers = ['Username', 'Wallet Address', 'Total Purchased', 'Purchase Count', 'First Purchase', 'Last Purchase'];
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => [
          row.username || '',
          row.walletAddress || '',
          row.totalPurchased || '',
          row.purchaseCount || '',
          row.firstPurchase ? new Date(row.firstPurchase).toLocaleString() : '',
          row.lastPurchase ? new Date(row.lastPurchase).toLocaleString() : ''
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `credits_deposit_per_user_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Finance
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Credit Deposit <span className="text-gradient-ginox">User</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">View credits deposit summary per user</p>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Username / WalletAddress</label>
            <input
              type="text"
              value={tempFilters.username}
              onChange={(e) => handleTempFilterChange('username', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              placeholder="Search…"
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Page Size</label>
            <select
              value={tempFilters.pageSize}
              onChange={(e) => handleTempFilterChange('pageSize', parseInt(e.target.value))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Page</label>
            <select
              value={tempFilters.page}
              onChange={(e) => handleTempFilterChange('page', parseInt(e.target.value))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={applyFilters} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
            Apply Filters
          </button>
          <button onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
          <button type="button" onClick={exportToExcel} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Export to Excel
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Loading...</p>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <SortableTableHeader
                    sortKey="username"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Username
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="totalPurchased"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Total Purchased
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="purchaseCount"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Purchase Count
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="firstPurchase"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    First Purchase
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="lastPurchase"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Last Purchase
                  </SortableTableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="border-b border-border/40 px-5 py-3.5 text-sm text-center text-muted-foreground">
                      No data found
                    </td>
                  </tr>
                ) : (
                  sortedData.map((item, index) => (
                    <tr key={`${item.username}-${index}`} className="group hover:bg-white/[0.02] transition-colors">
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                        <div>{item.username}</div>
                        <div className="text-xs text-muted-foreground/80">
                          <CopyHash value={item.walletAddress} href={bscAddressUrl(item.walletAddress)} head={6} tail={4} />
                        </div>
                      </td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.totalPurchased}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.purchaseCount}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{formatDate(item.firstPurchase)}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{formatDate(item.lastPurchase)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default CreditsDepositPerUser;
